import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import { Box, ButtonGroup, Heading, Icon, IconButton, Text, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconChevronLeftOutline, IconChevronRightOutline } from '@zillow/constellation-icons';

import { useRentalManagerUserFeatureLaunchWebApiHandler_notifications } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getConstellationIcon } from '../../utils/iconUtils';

import { FeatureLaunchNotification } from '../../model/NotificationBanner';

import { bannerBoxRecipe } from './styles';

import {
  emitDismissAllNotificationsEvent,
  emitDismissNotificationEvent,
  emitDisplayFeatureNotificationEvent,
  emitLearnMoreEvent,
  EVENT_IDS as FEATURE_NOTIFICATION_EVENT_IDS,
} from '../../analytics/events/featureNotifications';
import { useAuth } from '../AuthProvider';

import type { ReactNode } from 'react';

type Notification = {
  url: string;
  icon: string;
  name: string;
  ctaText: string;
  description: string;
  title: string;
  v10Icon?: string;
};

const NotificationBanner = () => {
  const { user } = useAuth();

  const [notifications, setNotifications] = useState<Array<Notification>>([]);
  const [iconComponent, setIconComponent] = useState<ReactNode>(null);
  const [index, setIndex] = useState(0);

  const { data: { notifications: notificationsData } = {}, isPending } =
    useRentalManagerUserFeatureLaunchWebApiHandler_notifications({});

  useEffect(() => {
    if (!isPending && Array.isArray(notificationsData)) {
      const newNotifications = notificationsData
        .map((data) => FeatureLaunchNotification(data) as Notification)
        .filter((notif) => Cookies.get(`ZRM_FEATURE_NOTIF_${notif.name}`) === undefined);
      setNotifications(newNotifications);
      if (newNotifications.length > 0) {
        emitDisplayFeatureNotificationEvent({ user: user! });
      }
    }
  }, [isPending]);

  const notification = notifications && notifications[index];

  useEffect(() => {
    const iconName = notification?.v10Icon || notification?.icon;

    if (!iconName) {
      setIconComponent(null);
      return;
    }

    const IconComponent = getConstellationIcon({
      iconName,
    });

    setIconComponent(IconComponent ? <Icon render={<IconComponent />} /> : null);
  }, [notification?.v10Icon, notification?.icon]);

  const addCookie = (notifType: string) => {
    // @ts-expect-error - Cookies.set expects string value, but withConverter allows boolean to be written as-is
    Cookies.withConverter({ write: (_) => _ }).set(`ZRM_FEATURE_NOTIF_${notifType}`, true);
  };

  const handlePreviousNotification = () => {
    addCookie(notifications[index].name);
    const newIndex = index <= 0 ? notifications.length - 1 : index - 1;
    setIndex(newIndex);
  };

  const handleNextNotification = () => {
    addCookie(notifications[index].name);
    const newIndex = index >= notifications.length - 1 ? 0 : index + 1;
    setIndex(newIndex);
  };

  const handleDismissAll = () => {
    for (let i = 0; i < notifications.length; i++) {
      addCookie(notifications[i].name);
    }
    setNotifications([]);
    emitDismissAllNotificationsEvent({ user: user! });
  };

  const handleDismiss = () => {
    addCookie(notifications[index].name);
    setNotifications([]);
    emitDismissNotificationEvent({ user: user! });
  };

  const handleCTAClick = () => {
    // Map notification title to trigger object for analytics
    const notificationTitle = notification?.title?.trim() || '';
    let triggerObject = 'no_trigger_object';

    if (
      notificationTitle === 'Introducing total prices on listings.' ||
      notificationTitle === 'Introducing total monthly prices on listings.'
    ) {
      if (user?.isEnterpriseCustomer) {
        triggerObject = 'zram_properties_page';
      } else {
        triggerObject = 'zrm_properties_page';
      }
    } else if (notificationTitle === 'Protect your account') {
      triggerObject = '2fa_setup';
    }

    emitLearnMoreEvent({ user: user!, triggerObject });
    window.open(notification.url, '_blank', 'noopener, noreferrer');
  };

  if (isPending || !notification || !user) {
    return null;
  }

  return (
    <Box
      data-testid="NotificationBanner"
      data-eventid={notifications.length > 0 ? FEATURE_NOTIFICATION_EVENT_IDS.DISPLAY_FEATURE_NOTIFICATION : undefined}
      data-eventdescription="View feature notification"
      css={bannerBoxRecipe.raw({ isEnterpriseCustomer: user.isEnterpriseCustomer })}
    >
      <UpsellBanner
        action={
          notification.ctaText &&
          notification.url && (
            <TextButton onClick={handleCTAClick} data-eventid={FEATURE_NOTIFICATION_EVENT_IDS.LEARN_MORE}>
              {notification.ctaText}
            </TextButton>
          )
        }
        header={
          notification.title && (
            <Heading level={2} textStyle="body-lg-bold">
              {notification.title}
            </Heading>
          )
        }
        body={notification.description && <Text>{notification.description}</Text>}
        media={iconComponent}
        onOpenChange={handleDismiss}
        data-eventid={FEATURE_NOTIFICATION_EVENT_IDS.DISMISS_NOTIFICATION}
        data-eventdescription="Dismiss feature notification"
      />
      {notifications.length > 1 && (
        <Box
          css={{
            display: 'flex',
            backgroundColor: 'UpsellBanner.Root.soft.bg',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <TextButton
            textStyle="body-sm-bold"
            onClick={handleDismissAll}
            data-eventid={FEATURE_NOTIFICATION_EVENT_IDS.DISMISS_ALL_NOTIFICATIONS}
            css={{ paddingRight: '400' }}
          >
            Dismiss all
          </TextButton>
          <ButtonGroup aria-label="notification controls">
            <IconButton
              tone="brand"
              emphasis="bare"
              onClick={handlePreviousNotification}
              icon={
                <Icon>
                  <IconChevronLeftOutline />
                </Icon>
              }
              title="Previous"
            />
            <IconButton
              tone="brand"
              emphasis="bare"
              onClick={handleNextNotification}
              icon={
                <Icon>
                  <IconChevronRightOutline />
                </Icon>
              }
              title="Next"
            />
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export { NotificationBanner };
export default NotificationBanner;
